import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';
import { getControleDescargaAuditorias } from '@spark/services/descarga';
import { notification } from '@spark/ui-components/utils/notification';

const auditoriaFilterConfig = [
  {
    id: 'TipoAcao',
    type: 'text',
    label: 'Tipo Acao',
    placeholder: 'Digite o tipo da acao'
  },
  {
    id: 'NomeColuna',
    type: 'text',
    label: 'Nome Coluna',
    placeholder: 'Digite a coluna'
  },
  {
    id: 'Usuario',
    type: 'text',
    label: 'Usuario',
    placeholder: 'Digite o usuario'
  },
  {
    id: 'NumeroCarga',
    type: 'text',
    label: 'Numero Carga',
    placeholder: 'Digite o numero da carga'
  },
  {
    id: 'NumeroPedido',
    type: 'text',
    label: 'Numero Pedido',
    placeholder: 'Digite o numero do pedido'
  },
  {
    id: 'CNPJCliente',
    type: 'text',
    label: 'CNPJ Cliente',
    placeholder: 'Digite o CNPJ do cliente'
  },
  {
    id: 'NomeCliente',
    type: 'text',
    label: 'Nome Cliente',
    placeholder: 'Digite o nome do cliente'
  },
  {
    id: 'DataAcaoInicio',
    type: 'datePicker',
    label: 'Data Acao Inicio',
    placeholder: 'Selecione o inicio',
    showTimeSelect: true
  },
  {
    id: 'DataAcaoFim',
    type: 'datePicker',
    label: 'Data Acao Fim',
    placeholder: 'Selecione o fim',
    showTimeSelect: true
  }
];

const renderText = (maxLength = 22) => ({ getValue }) => (
  <TruncatedText value={getValue() || '-'} maxLength={maxLength} />
);

const formatarDataHora = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const toApiDateTime = (value) => {
  if (!value) return undefined;
  if (value instanceof Date) return value.toISOString();
  const normalized = String(value).trim();
  if (!normalized) return undefined;

  const localDateTime = normalized.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/);
  if (localDateTime) {
    const [, year, month, day, hour, minute] = localDateTime;
    return `${year}-${month}-${day}T${hour}:${minute}:00`;
  }

  return normalized;
};

const getRowCode = (row, codeKey, rawKeys = []) => {
  const raw = row?.raw || {};
  const value = row?.[codeKey] ?? raw[codeKey] ?? rawKeys.map((key) => raw[key]).find((item) => item !== undefined && item !== null && item !== '');
  return value === '-' ? undefined : value;
};

const normalizeAuditoriaResponse = (response) => {
  const data = response?.data || response || {};
  const registrosRaw = Array.isArray(data.registros)
    ? data.registros
    : Array.isArray(data.Registros)
      ? data.Registros
      : [];

  return {
    totalRegistros: data.totalRegistros ?? data.TotalRegistros ?? registrosRaw.length,
    registros: registrosRaw.map((item, index) => ({
      id: item.id ?? item.Id ?? `${index}-${item.dataAcao || item.DataAcao || 'auditoria'}`,
      tipoAcao: item.tipoAcao ?? item.TipoAcao ?? item.acao ?? item.Acao ?? '-',
      nomeColuna: item.nomeColuna ?? item.NomeColuna ?? item.coluna ?? item.Coluna ?? '-',
      valorAnterior: item.valorAnterior ?? item.ValorAnterior ?? item.valorAntigo ?? item.ValorAntigo ?? '-',
      valorNovo: item.valorNovo ?? item.ValorNovo ?? '-',
      usuario: item.usuario ?? item.Usuario ?? '-',
      dataAcao: item.dataAcao ?? item.DataAcao ?? item.dataHora ?? item.DataHora ?? null,
      pedCodigo: item.pedCodigo ?? item.PedCodigo ?? '-',
      cliCodigo: item.cliCodigo ?? item.CliCodigo ?? '-',
      carCodigo: item.carCodigo ?? item.CarCodigo ?? '-',
      numeroCarga: item.numeroCarga ?? item.NumeroCarga ?? '-',
      numeroPedido: item.numeroPedido ?? item.NumeroPedido ?? '-',
      cnpjCliente: item.cnpjCliente ?? item.CNPJCliente ?? item.cnpjDestinatario ?? item.CNPJDestinatario ?? '-',
      nomeCliente: item.nomeCliente ?? item.NomeCliente ?? item.nomeDestinatario ?? item.NomeDestinatario ?? '-'
    }))
  };
};

const auditoriaColumns = [
  {
    id: 'dataAcao',
    accessorKey: 'dataAcao',
    header: 'Data Acao',
    size: 180,
    cell: ({ getValue }) => <span>{formatarDataHora(getValue())}</span>
  },
  {
    id: 'tipoAcao',
    accessorKey: 'tipoAcao',
    header: 'Tipo Acao',
    size: 140,
    cell: renderText(18)
  },
  {
    id: 'nomeColuna',
    accessorKey: 'nomeColuna',
    header: 'Coluna',
    size: 180,
    cell: renderText(22)
  },
  {
    id: 'valorAnterior',
    accessorKey: 'valorAnterior',
    header: 'Valor Anterior',
    size: 220,
    cell: renderText(28)
  },
  {
    id: 'valorNovo',
    accessorKey: 'valorNovo',
    header: 'Valor Novo',
    size: 220,
    cell: renderText(28)
  },
  {
    id: 'usuario',
    accessorKey: 'usuario',
    header: 'Usuario',
    size: 170,
    cell: renderText(22)
  },
  {
    id: 'pedCodigo',
    accessorKey: 'pedCodigo',
    header: 'Ped Codigo',
    size: 120,
    cell: renderText(14)
  },
  {
    id: 'cliCodigo',
    accessorKey: 'cliCodigo',
    header: 'Cli Codigo',
    size: 150,
    cell: renderText(18)
  },
  {
    id: 'carCodigo',
    accessorKey: 'carCodigo',
    header: 'Car Codigo',
    size: 120,
    cell: renderText(14)
  },
  {
    id: 'numeroCarga',
    accessorKey: 'numeroCarga',
    header: 'Numero Carga',
    size: 140,
    cell: renderText(16)
  },
  {
    id: 'numeroPedido',
    accessorKey: 'numeroPedido',
    header: 'Numero Pedido',
    size: 140,
    cell: renderText(16)
  },
  {
    id: 'cnpjCliente',
    accessorKey: 'cnpjCliente',
    header: 'CNPJ Cliente',
    size: 170,
    cell: renderText(18)
  },
  {
    id: 'nomeCliente',
    accessorKey: 'nomeCliente',
    header: 'Nome Cliente',
    size: 220,
    cell: renderText(28)
  }
];

const DescargaAuditoriaModal = ({ show, row, onClose, geral = false }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [filtros, setFiltros] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  const rowCodes = useMemo(() => ({
    PedCodigo: getRowCode(row, 'pedCodigo', ['pedCodigo', 'PedCodigo', 'codigoPedido', 'CodigoPedido']),
    CliCodigo: getRowCode(row, 'cliCodigo', ['cliCodigo', 'CliCodigo', 'cnpjDestinatario', 'CNPJDestinatario']),
    CarCodigo: getRowCode(row, 'carCodigo', ['carCodigo', 'CarCodigo', 'codigoCarga', 'CodigoCarga'])
  }), [row]);

  const fetchAuditorias = useCallback(async () => {
    if (!show || (!geral && !row)) return;

    try {
      setLoading(true);
      const response = await getControleDescargaAuditorias({
        ...filtros,
        DataAcaoInicio: toApiDateTime(filtros.DataAcaoInicio),
        DataAcaoFim: toApiDateTime(filtros.DataAcaoFim),
        ...(!geral ? {
          PedCodigo: rowCodes.PedCodigo,
          CliCodigo: rowCodes.CliCodigo,
          CarCodigo: rowCodes.CarCodigo
        } : {}),
        PaginaAtual: pagination.pageIndex + 1,
        QuantidadePorPagina: pagination.pageSize
      });
      const auditorias = normalizeAuditoriaResponse(response);
      setData(auditorias.registros);
      setTotalRegistros(auditorias.totalRegistros);
    } catch (error) {
      console.error('Erro ao carregar auditoria da descarga:', error);
      notification.error('Erro ao carregar auditoria da descarga.');
      setData([]);
      setTotalRegistros(0);
    } finally {
      setLoading(false);
    }
  }, [filtros, geral, pagination.pageIndex, pagination.pageSize, row, rowCodes, show]);

  useEffect(() => {
    fetchAuditorias();
  }, [fetchAuditorias]);

  useEffect(() => {
    if (!show) return;
    setPagination({ pageIndex: 0, pageSize: 10 });
    setFiltros({});
  }, [geral, show, row?.id]);

  const handleFilterApply = useCallback((nextFilters = {}) => {
    setFiltros(nextFilters);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handlePaginationChange = useCallback((next) => {
    setPagination((prev) => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev, pageIndex: next };
      if (next && typeof next === 'object') return { ...prev, ...next };
      return prev;
    });
  }, []);

  return (
    <Modal show={show} onHide={onClose} animation={false} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {geral
            ? 'Auditoria Geral da Descarga'
            : `Auditoria da Descarga ${row?.numeroCarga && row.numeroCarga !== '-' ? `- Carga ${row.numeroCarga}` : ''}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FilterPanel
          filters={auditoriaFilterConfig}
          values={filtros}
          onApply={handleFilterApply}
          title="Filtros de Auditoria"
          loading={loading}
          defaultCollapsed={true}
        />

        {data.length === 0 && !loading ? (
          <div className="text-muted">
            {geral
              ? 'Nenhum registro de auditoria encontrado.'
              : 'Nenhum registro de auditoria encontrado para esta descarga.'}
          </div>
        ) : (
          <TanStackTable
            key={`descarga-auditoria-${row?.id || 'modal'}-${pagination.pageIndex}-${pagination.pageSize}`}
            columns={auditoriaColumns}
            data={data}
            loading={loading}
            isSortable={true}
            withPagination={true}
            manualPagination={true}
            totalItems={totalRegistros}
            controlledPagination={pagination}
            onPaginationChange={handlePaginationChange}
            pageSizeOptions={[10, 25, 50, 100]}
            enableColumnConfig={true}
            columnConfigKey="descarga-auditoria-columns"
            enableExportExcel={true}
            exportFileName="descarga-auditoria"
            className="mt-3"
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DescargaAuditoriaModal;
